import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Archive, RotateCcw, Trash2, MapPin } from "lucide-react";
import api from "../lib/api";
import { toast } from "./ui/sonner";
import ConfirmDialog from "./ConfirmDialog";

const locationLabels = {
  dubai: "Dubai",
  north_cyprus: "North Cyprus",
  germany: "Germany",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  } catch {
    return iso.slice(0, 10);
  }
};

export default function ArchiveModal({ open, onClose, onAnyChange }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [confirm, setConfirm] = useState({ open: false, asset: null });

  const fetchArchive = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/assets/archived");
      setItems(data || []);
    } catch (err) {
      toast.error("Failed to load archive", { description: err.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchArchive();
  }, [open, fetchArchive]);

  const handleRestore = async (asset) => {
    try {
      await api.post(`/assets/${asset.id}/restore`);
      toast.success(`Restored "${asset.name}"`, { description: "Back in your active portfolio." });
      await fetchArchive();
      onAnyChange?.();
    } catch (err) {
      toast.error("Failed to restore", { description: err.response?.data?.detail });
    }
  };

  const handlePermanentDelete = async () => {
    const asset = confirm.asset;
    setConfirm({ open: false, asset: null });
    if (!asset) return;
    try {
      await api.delete(`/assets/${asset.id}?permanent=true`);
      toast.success(`Permanently deleted "${asset.name}"`);
      await fetchArchive();
      onAnyChange?.();
    } catch (err) {
      toast.error("Failed to delete", { description: err.response?.data?.detail });
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          data-testid="archive-modal"
        >
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />

          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-3xl max-h-[85vh] flex flex-col bg-[#0A0A0A] border border-white/[0.12] shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.10]">
              <div className="flex items-center gap-3">
                <Archive className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                <div>
                  <p className="text-[10px] tracking-[0.18em] uppercase text-white/65 mb-0.5">Vault</p>
                  <h3 className="font-serif text-xl text-white">Archived Properties</h3>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="archive-close-btn"
                className="text-white/65 hover:text-white transition-colors p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : items.length === 0 ? (
                <div className="text-center py-16">
                  <Archive className="w-10 h-10 text-white/10 mx-auto mb-3" strokeWidth={1} />
                  <p className="text-white/60 text-sm mb-1">No archived properties</p>
                  <p className="text-white/60 text-xs">Properties you delete will appear here. You can restore them later or remove them permanently.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {items.map((a) => (
                    <div
                      key={a.id}
                      data-testid={`archived-asset-${a.id}`}
                      className="flex items-center justify-between gap-4 bg-white/[0.02] border border-white/[0.08] hover:border-white/[0.12] transition-colors px-4 py-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-sm font-medium truncate">{a.name || "Untitled property"}</p>
                        <div className="flex items-center gap-3 text-[11px] text-white/60 mt-0.5">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {locationLabels[a.location] || (a.location === "other" ? (a.country_name || "Global") : a.location) || "—"}
                          </span>
                          <span>archived {formatDate(a.archived_at)}</span>
                          {a.purchase_price ? (
                            <span className="font-mono">
                              {a.currency} {Number(a.purchase_price).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => handleRestore(a)}
                          data-testid={`restore-asset-${a.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-[0.1em] uppercase border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
                        >
                          <RotateCcw className="w-3 h-3" /> Restore
                        </button>
                        <button
                          onClick={() => setConfirm({ open: true, asset: a })}
                          data-testid={`permanent-delete-${a.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-[0.1em] uppercase border border-red-500/20 text-red-300/80 hover:bg-red-500/10 hover:border-red-500/40 transition-colors"
                          title="Permanently delete this property"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!loading && items.length > 0 && (
              <div className="px-6 py-3 border-t border-white/[0.10] bg-black/30 text-[10px] text-white/65 tracking-wider">
                Restoring brings the property back to your active portfolio with all its data, documents and milestones intact.
              </div>
            )}
          </motion.div>

          <ConfirmDialog
            open={confirm.open}
            title="Permanently delete this property?"
            description={
              <>
                This will permanently remove <span className="text-white font-medium">{confirm.asset?.name}</span> from your account, including all linked documents and payment milestones. <span className="text-red-400/80">This cannot be undone.</span>
              </>
            }
            confirmLabel="Delete Forever"
            tone="danger"
            onCancel={() => setConfirm({ open: false, asset: null })}
            onConfirm={handlePermanentDelete}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
